// ─────────────────────────────────────────────
//  Cascade AI — Tool Registry
// ─────────────────────────────────────────────

import EventEmitter from 'node:events';
import path from 'node:path';
import ignoreFactory, { type Ignore } from 'ignore';

// `ignore` is published as CJS. Under `moduleResolution: NodeNext` the
// default import surfaces the module namespace rather than the callable
// factory, so normalise to whichever shape the runtime hands us.
const ignore: (opts?: unknown) => Ignore =
  (ignoreFactory as unknown as { default?: (opts?: unknown) => Ignore }).default ??
  (ignoreFactory as unknown as (opts?: unknown) => Ignore);
import type { ToolDefinition, ToolExecuteOptions, ToolsConfig } from '../types.js';
import { DEFAULT_APPROVAL_REQUIRED } from '../constants.js';
import type { BaseTool } from './base.js';
import { ShellTool } from './shell.js';
import { FileReadTool, FileWriteTool, FileEditTool, FileDeleteTool, FileListTool } from './file.js';
import { GitTool } from './git.js';
import { GitHubTool } from './github.js';
import { BrowserTool } from './browser.js';
import { ImageAnalyzeTool } from './image.js';
import { PDFCreateTool } from './pdf.js';
import { CodeInterpreterTool } from './interpreter.js';
import { PeerCommunicationTool } from './peer.js';
import { WebSearchTool } from './web-search.js';
import { GlobTool } from './glob.js';
import { GrepTool } from './grep.js';
import { WebFetchTool } from './web-fetch.js';
import { McpClient } from '../mcp/client.js';
import { McpToolWrapper } from './mcp.js';

// ── Plugin System (Roadmap Stub) ──────────────────────────────────────────
//
// Future plugin support. Plugins bundle one or more tools together with
// optional lifecycle hooks. Use `registry.registerPlugin(plugin)` to register.
// This is a stub — roadmap tracked in README.md under Roadmap.

/**
 * A ToolPlugin bundles one or more custom tools that extend Cascade's capabilities.
 * Plugins are loaded via `registerPlugin()` and behave like built-in tools.
 *
 * @example
 * const myPlugin: ToolPlugin = {
 *   name: 'my-custom-tools',
 *   version: '1.0.0',
 *   tools: [new MyCustomTool()],
 *   onRegister: (registry) => console.log('Plugin registered'),
 * };
 * registry.registerPlugin(myPlugin);
 */
export interface ToolPlugin {
  /** Unique plugin identifier */
  name: string;
  /** Semantic version string */
  version: string;
  /** One or more tools this plugin provides */
  tools: BaseTool[];
  /** Called once when the plugin is registered */
  onRegister?: (registry: ToolRegistry) => void;
}

export class ToolRegistry extends EventEmitter {
  private tools: Map<string, BaseTool> = new Map();
  private config: ToolsConfig;
  private ignoreMatcher: Ignore = ignore();
  private workspaceRoot: string;
  /** Loaded plugins, keyed by plugin name */
  private plugins: Map<string, ToolPlugin> = new Map();

  constructor(config: ToolsConfig, workspaceRoot: string = process.cwd()) {
    super();
    this.config = config;
    this.workspaceRoot = workspaceRoot;
    this.registerDefaults();
  }

  register(tool: BaseTool): void {
    this.tools.set(tool.name, tool);
    this.emit('tool:added', tool.name);
  }

  /**
   * Wait until a named tool is registered, resolving immediately if it already exists.
   * T3 workers can call this after encountering a missing-tool error to resume
   * automatically once T2 synthesizes the tool.
   */
  waitForTool(toolName: string, timeoutMs = 60_000): Promise<void> {
    if (this.tools.has(toolName)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.off('tool:added', handler);
        reject(new Error(`Timeout waiting for tool: ${toolName}`));
      }, timeoutMs);

      const handler = (name: string) => {
        if (name === toolName) {
          clearTimeout(timer);
          this.off('tool:added', handler);
          resolve();
        }
      };
      this.on('tool:added', handler);
    });
  }

  /**
   * Register a ToolPlugin, loading all its tools into the registry.
   * Each tool is configured with the current workspace root.
   * Duplicate plugin names are silently ignored.
   *
   * @example
   * registry.registerPlugin(myPlugin);
   */
  registerPlugin(plugin: ToolPlugin): void {
    if (this.plugins.has(plugin.name)) return;
    this.plugins.set(plugin.name, plugin);
    for (const tool of plugin.tools) {
      tool.setWorkspaceRoot(this.workspaceRoot);
      this.register(tool);
    }
    plugin.onRegister?.(this);
  }

  /** Returns the names of all registered plugins */
  getRegisteredPlugins(): string[] {
    return Array.from(this.plugins.keys());
  }

  /** Registers all tools from an MCP client */
  registerMcpTools(mcpClient: McpClient): void {
    const definitions = mcpClient.getToolDefinitions();
    for (const def of definitions) {
      // Definitions from McpClient.getToolDefinitions() are prefixed as
      // `mcp::<serverName>::<toolName>` — three parts, not four. Previously
      // this destructured [,, serverName, toolName] which silently dropped
      // every MCP tool (toolName was always undefined and the `continue`
      // below filtered them all out).
      const [, serverName, toolName] = def.name.split('::');
      if (!serverName || !toolName) continue;

      const wrapper = new McpToolWrapper(
        mcpClient,
        serverName,
        toolName,
        def.description.replace(`[MCP:${serverName}] `, ''),
        def.inputSchema,
      );
      wrapper.setWorkspaceRoot(this.workspaceRoot);
      this.register(wrapper);
    }
  }

  setIgnoredPaths(patterns: string[]): void {
    // `ignore` mirrors .gitignore semantics: supports negation, globs, dirs.
    // Using the library eliminates the old substring bug where `node_modules`
    // would also match `mynodemodules.js`.
    this.ignoreMatcher = ignore().add(patterns);
  }

  getToolDefinitions(): ToolDefinition[] {
    return Array.from(this.tools.values()).map((t) => t.getDefinition());
  }

  getTool(name: string): BaseTool | undefined {
    return this.tools.get(name);
  }

  hasTool(name: string): boolean {
    return this.tools.has(name);
  }

  requiresApproval(toolName: string): boolean {
    const defaults = DEFAULT_APPROVAL_REQUIRED as string[];
    const configured = this.config.requireApprovalFor;
    return defaults.includes(toolName) || configured.includes(toolName);
  }

  isDangerous(toolName: string): boolean {
    return this.tools.get(toolName)?.isDangerous() ?? false;
  }

  async execute(
    toolName: string,
    input: Record<string, unknown>,
    options: ToolExecuteOptions,
  ): Promise<string> {
    const tool = this.tools.get(toolName);
    if (!tool) throw new Error(`Tool not found: ${toolName}`);

    // Enforce .cascadeignore for file operations
    if (this.isFileOperation(toolName)) {
      const filePath = (input['path'] as string | undefined) ?? '';
      if (this.isIgnored(filePath)) {
        throw new Error(`Access denied: ${filePath} is in .cascadeignore`);
      }
    }

    return tool.execute(input, options);
  }

  private registerDefaults(): void {
    const tools: BaseTool[] = [
      new ShellTool(this.config.shellAllowlist, this.config.shellBlocklist),
      new FileReadTool(),
      new FileWriteTool(),
      new FileEditTool(),
      new FileDeleteTool(),
      new FileListTool(),
      new GitTool(),
      new GitHubTool(),
      new ImageAnalyzeTool(),
      new PDFCreateTool(),
      new CodeInterpreterTool(),
      new PeerCommunicationTool(),
      new WebSearchTool(this.config.webSearch),
      new GlobTool(),
      new GrepTool(),
      new WebFetchTool(),
    ];

    for (const tool of tools) {
      tool.setWorkspaceRoot(this.workspaceRoot);
      this.register(tool);
    }

    if (this.config.browserEnabled) {
      const browser = new BrowserTool();
      browser.setWorkspaceRoot(this.workspaceRoot);
      this.register(browser);
    }
  }

  private isFileOperation(toolName: string): boolean {
    return ['file_read', 'file_write', 'file_edit', 'file_delete'].includes(toolName);
  }

  private isIgnored(filePath: string): boolean {
    if (!filePath) return false;
    const abs = path.resolve(this.workspaceRoot, filePath);
    const rel = path.relative(this.workspaceRoot, abs);
    // Any path outside the workspace is treated as ignored (defence in depth;
    // the dedicated path-sandbox guards in each file tool also reject these).
    if (!rel || rel.startsWith('..') || path.isAbsolute(rel)) return true;
    // `ignore` requires POSIX-style separators.
    const posixRel = rel.split(path.sep).join('/');
    return this.ignoreMatcher.ignores(posixRel);
  }
}
